import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Image, TouchableOpacity, View } from "react-native-web";
import Appstyles from "../../Constants/AppStyles";
import AppImages from "../../Constants/AppImages";
import { useNavigate, useLocation } from "react-router-dom";
import { useDrawer } from "../Drawer/DrawerContext";

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom vehicle icons
const createVehicleIcon = (color = "red") => {
  return L.divIcon({
    html: `<div style="
      background-color: ${color};
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    "></div>`,
    className: "vehicle-marker",
    iconSize: [20, 20],
  });
};

// Component to fit map to markers
const FitBoundsToMarkers = ({ markers }) => {
  const map = useMap();

  useEffect(() => {
    if (markers.length > 0) {
      const group = new L.FeatureGroup(
        markers.map((marker) => L.marker(marker.position))
      );
      map.fitBounds(group.getBounds(), { padding: [50, 50] });
    }
  }, [markers, map]);

  return null;
};

// Vehicle Marker Component
const VehicleMarker = ({ position, vehicle, onMarkerClick, autoOpen }) => {
  const icon = createVehicleIcon(vehicle.color);
  const markerRef = useRef(null);

  // Auto-open this marker's device-details popup (PDF DeviceDetails/View route).
  useEffect(() => {
    if (autoOpen && markerRef.current) {
      markerRef.current.openPopup();
    }
  }, [autoOpen]);

  return (
    <Marker position={position} icon={icon} ref={markerRef}>
      <Popup>
        <div style={{ minWidth: 200 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <h4 style={{ margin: 0 }}>{vehicle.name}</h4>
            <button
              onClick={() => onMarkerClick(vehicle)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              ⋮
            </button>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <span>Speed:</span>
            <strong>{vehicle.speed} Km/h</strong>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <span>Status:</span>
            <strong>{vehicle.status}</strong>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <span>Satellites:</span>
            <strong>{vehicle.satellites}</strong>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <span>Last Seen:</span>
            <strong>{vehicle.lastSeen}</strong>
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

const DashboardMap = () => {
  const [selectedTab, setSelectedTab] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isViewVisible, setIsViewVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { openDrawer, closeDrawer, toggleDrawer } = useDrawer();
  // Vehicle data
  const vehicles = [
    {
      id: 1,
      name: "Vehicle 1",
      position: [37.78825, -122.4324],
      color: "red",
      speed: "25",
      status: "Online / Running",
      satellites: "9",
      lastSeen: "2023-01-06 21:05:05",
      type: "parked",
    },
    {
      id: 2,
      name: "Vehicle 2",
      position: [37.88825, -122.4324],
      color: "green",
      speed: "45",
      status: "Running",
      satellites: "12",
      lastSeen: "2023-01-06 20:05:05",
      type: "running",
    },
    {
      id: 3,
      name: "Vehicle 3",
      position: [37.58625, -122.5824],
      color: "blue",
      speed: "0",
      status: "Idle",
      satellites: "8",
      lastSeen: "2023-01-06 19:05:05",
      type: "idle",
    },
  ];

  // Route coordinates
  const routeCoordinates = [
    [37.78825, -122.4324],
    [37.967, -122.453],
  ];

  const handleTabPress = useCallback((tab) => {
    setSelectedTab(tab);
  }, []);

  const handleMarkerClick = useCallback((vehicle) => {
    setSelectedVehicle(vehicle);
    setModalVisible(true);
  }, []);

  // Auto-open the "Vehicle 1 Details" modal when the PDF route
  // /Dashboard/DashboardMap/Vehicle/View is opened directly.
  useEffect(() => {
    if (location.pathname === "/Dashboard/DashboardMap/Vehicle/View") {
      setSelectedVehicle(vehicles[0]);
      setModalVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleView = useCallback(() => {
    setIsViewVisible((prev) => !prev);
  }, []);

  const handleNav = useCallback((page) => {
    console.log("Navigating to:", page);
    // Add your navigation logic here
  }, []);

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      {/* <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 20px",
          backgroundColor: "white",
          boxShadow: "0 2px 10px rgba(156, 73, 0, 0.1)",
          height: "60px",
        }}
      >
        <div>
          <button
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
            }}
          >
            ☰
          </button>
        </div>

        <img
          src="/path-to-your-logo.png"
          alt="Logo"
          style={{ height: "30px" }}
        />

        <div style={{ display: "flex", gap: "15px" }}>
          <button
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            🔔
          </button>
          <button
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            ⚙️
          </button>
        </div>
      </header> */}

      <View
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          width: "100%",
          flexDirection: "row",
          alignItems: "center",
          backgroundColor: "white",
          height: 58,
          justifyContent: "space-between",
          paddingHorizontal: 20,
          shadowColor: "#9C4900",
          shadowOffset: { width: 10, height: 1 },
          shadowOpacity: 10,
          shadowRadius: 10,
          elevation: 10,
          zIndex: 1000,
        }}
      >
        <View style={Appstyles.fullAlignment}>
          <TouchableOpacity onPress={openDrawer}>
            <Image
              source={AppImages.Drawer}
              style={{
                width: 21,
                height: 22,
                resizeMode: "contain",
                marginLeft: 30,
              }}
            />
          </TouchableOpacity>
        </View>
        <Image
          source={AppImages.LoginLogo}
          style={[{ width: 120, height: 36, resizeMode: "contain" }]}
        />
        <View style={{ flexDirection: "row" }}>
          <TouchableOpacity onPress={() => navigate("/Dashboard/DashboardMap/Alerts/View")}>
            <Image
              source={AppImages.VechicleNoti}
              style={Appstyles.VehicleImage}
            />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => navigate("/Dashboard/DashboardMap/Alerts/View")}>
            <Image source={AppImages.Bellnoti} style={Appstyles.BellImage} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Map Container */}
      <div style={{ flex: 1, position: "relative" }}>
        <MapContainer
          center={[37.78825, -122.4324]}
          zoom={13}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          <FitBoundsToMarkers markers={vehicles} />

          {/* Route Polyline */}
          <Polyline
            positions={routeCoordinates}
            color="orange"
            weight={5}
            opacity={0.7}
          />

          {/* Vehicle Markers */}
          {vehicles.map((vehicle) => (
            <VehicleMarker
              key={vehicle.id}
              position={vehicle.position}
              vehicle={vehicle}
              onMarkerClick={handleMarkerClick}
              autoOpen={
                vehicle.id === 1 &&
                location.pathname ===
                  "/Dashboard/DashboardMap/DeviceDetails/View"
              }
            />
          ))}
        </MapContainer>

        {/* Bottom Controls */}
        <div
          style={{
            position: "absolute",
            bottom: "20px",
            left: "0",
            right: "0",
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              padding: "0 20px",
              pointerEvents: "none",
            }}
          >
            {/* Left Side - Vehicle Counts */}
            {isViewVisible && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  pointerEvents: "auto",
                }}
              >
                <button
                  onClick={() => handleNav(0)}
                  style={styles.controlButton}
                >
                  <span style={styles.buttonIcon}>🚗</span>
                  <span style={styles.buttonText}>140</span>
                </button>
                <button
                  onClick={() => handleNav(1)}
                  style={styles.controlButton}
                >
                  <span style={styles.buttonIcon}>🔴</span>
                  <span style={styles.buttonText}>100</span>
                </button>
              </div>
            )}

            {/* Right Side - Tools */}
            {isViewVisible && (
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  pointerEvents: "auto",
                }}
              >
                <button
                  onClick={() => handleNav(2)}
                  style={styles.controlButton}
                >
                  <span style={styles.buttonIcon}>📊</span>
                  <span style={styles.buttonText}>120</span>
                </button>
                <button
                  onClick={() => handleNav(3)}
                  style={styles.controlButton}
                >
                  <span style={styles.buttonIcon}>🛠️</span>
                  <span style={styles.buttonText}>Tools</span>
                </button>
              </div>
            )}

            {/* Toggle Button - Always visible */}
            <div style={{ pointerEvents: "auto" }}>
              <button
                onClick={toggleView}
                style={{
                  ...styles.controlButton,
                  width: "50px",
                  height: "50px",
                  borderRadius: "25px",
                  backgroundColor: isViewVisible ? "#fff" : "#f0f0f0",
                }}
              >
                <span style={styles.buttonIcon}>
                  {isViewVisible ? "🚗" : "📍"}
                </span>
              </button>
            </div>
          </div>

          {/* Legend */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "15px",
              padding: "10px 20px",
              backgroundColor: "white",
              marginTop: "10px",
              pointerEvents: "auto",
              borderRadius: "5px",
              margin: "10px 20px 0 20px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            }}
          >
            {[
              { color: "green", label: "Customer" },
              { color: "blue", label: "Running" },
              { color: "red", label: "Parked" },
              { color: "yellow", label: "Untracked" },
              { color: "brown", label: "Idle" },
            ].map((item, index) => (
              <div
                key={index}
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: item.color,
                  }}
                />
                <span style={{ fontSize: "12px" }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      {modalVisible && selectedVehicle && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h3>{selectedVehicle.name} Details</h3>
            <div style={styles.modalDetails}>
              <p>
                <strong>Speed:</strong> {selectedVehicle.speed} Km/h
              </p>
              <p>
                <strong>Status:</strong> {selectedVehicle.status}
              </p>
              <p>
                <strong>Satellites:</strong> {selectedVehicle.satellites}
              </p>
              <p>
                <strong>Last Seen:</strong> {selectedVehicle.lastSeen}
              </p>
            </div>
            <div style={styles.modalActions}>
              <button
                onClick={() => setModalVisible(false)}
                style={styles.closeButton}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const styles = {
  controlButton: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "45px",
    height: "45px",
    borderRadius: "22.5px",
    backgroundColor: "white",
    border: "none",
    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
    cursor: "pointer",
    fontSize: "12px",
  },
  buttonIcon: {
    fontSize: "16px",
    marginBottom: "2px",
  },
  buttonText: {
    fontSize: "10px",
    fontWeight: "bold",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
  },
  modalContent: {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "10px",
    minWidth: "300px",
    maxWidth: "500px",
  },
  modalDetails: {
    margin: "15px 0",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
  },
  closeButton: {
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: "5px",
    cursor: "pointer",
  },
};

export default DashboardMap;
